import axios from 'axios';

import { apiKey, unixSeconds, secureHashFor } from '@util/authInfo';
import { captureUnexpectedClientError } from '@util/sentry';
import { toErrorMessage } from '@util/toErrorMessage';

axios.defaults.baseURL = '/api';

let serverSessionTokenInterceptorId = null;

export function attachServerSessionToken(token) {
  if (!token) {
    return;
  }

  if (serverSessionTokenInterceptorId !== null) {
    axios.interceptors.request.eject(serverSessionTokenInterceptorId);
  }

  serverSessionTokenInterceptorId = axios.interceptors.request.use(
    (request) => {
      request.headers['server-session-token'] = token;
      return request;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
}

const requestWhitelist = [
  '/status/',
  '/admin-login/',
  '/athlete-login/',
  '/admin-passkey-login/options/',
  '/admin-passkey-login/verify/',
  '/athlete-passkey-login/options/',
  '/athlete-passkey-login/verify/',
  '/forgot-password/',
  '/password/',
];

axios.interceptors.request.use(
  (request) => {
    const timestamp = unixSeconds();
    request.headers['api-key'] = apiKey;
    request.headers['timestamp'] = timestamp;
    request.headers['secure-hash'] = secureHashFor(timestamp);

    if (
      requestWhitelist.includes(request.url) ||
      (request.url && request.url.indexOf('/verify-code/') > -1)
    ) {
      request.headers['internal'] = true;
    }

    return request;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => {
    if (response.data.response_code === undefined) {
      return response;
    }

    if (
      response.data.response_code === 2 &&
      response.data.response_message &&
      response.data.response_message === 'Back to Login'
    ) {
      return (window.location = `/app`);
    }

    if (response.data.response_code !== 0) {
      if (String(response.config?.url || '').includes('spotify')) {
        const url = String(response.config?.url || '');
        const isBackgroundPoll =
          /\/spotify-player\/?$/.test(url) ||
          url.includes('/spotify-player/devices');
        const message = String(response.data.response_message || '');
        const isRateLimited = /rate-limiting|too many requests/i.test(message);
        if (isBackgroundPoll || isRateLimited) {
          console.debug('Spotify soft error', url, response.data);
        } else {
          console.error('Spotify error', url, response.data);
        }
      }
      if (String(response.config?.url || '').includes('apple-music')) {
        console.error(
          'Apple Music error',
          response.config?.url,
          response.data,
        );
      }
      throw response.data.response_message;
    }
    return response;
  },
  (error) => {
    if (typeof error === 'string') {
      return Promise.reject(error);
    }

    if (String(error?.config?.url || '').includes('spotify')) {
      console.error(
        'Spotify error',
        error?.config?.url,
        error,
        error?.response?.data,
        error?.response,
      );
    }

    if (String(error?.config?.url || '').includes('apple-music')) {
      console.error(
        'Apple Music error',
        error?.config?.url,
        error,
        error?.response?.data,
        error?.response,
      );
    }

    captureUnexpectedClientError(error, { source: 'axios_interceptor' });

    return Promise.reject(
      toErrorMessage(error, 'There was a problem. Try again later.'),
    );
  },
);

export default axios;
